import { useState } from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Loader } from '@/components'
import { HolidayBalanceCard } from '@/features/internalHours/InternalHoursBalanceCard'
import { useHrInternalHoursEmployeeDetail } from '@/hooks'
import { HrEmployeeMonthAccordion } from './HrEmployeeMonthAccordion'

const CURRENT_YEAR = new Date().getFullYear()
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2]

type Props = {
  userId: number | string
}

export const HrEmployeeDetailContainer = ({ userId }: Props): JSX.Element => {
  const { t } = useTranslation('hrInternalHours')
  const [year, setYear] = useState<number>(CURRENT_YEAR)

  const { data, isLoading } = useHrInternalHoursEmployeeDetail(userId, { year })

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setYear(Number(event.target.value))
  }

  if (isLoading) {
    return (
      <Box my={10}>
        <Loader />
      </Box>
    )
  }

  if (!data) return <></>

  return (
    <Stack spacing={4}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack>
          <Typography variant="h5" fontWeight="bold">
            {data.first_name} {data.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.email}
          </Typography>
        </Stack>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('filters.year')}</InputLabel>
          <Select<number>
            value={year}
            label={t('filters.year')}
            onChange={handleYearChange}
          >
            {YEAR_OPTIONS.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <HolidayBalanceCard
        balance={data.holiday_balance}
        title={t('detail.balance.title')}
      />

      <Stack spacing={1}>
        <Typography variant="h6" fontWeight="bold">
          {t('detail.months.title')}
        </Typography>
        {data.months.length > 0 ? (
          data.months.map((month) => (
            <HrEmployeeMonthAccordion
              key={`${month.year}-${month.month}`}
              month={month}
            />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('detail.months.noData')}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}
